/**
 * Shared key-metric formulas (client + API).
 *
 * Airtable Companies fields to create (Number / Checkbox / Date):
 *   KeyMetricGrants
 *   KeyMetricSoftware
 *   KeyMetricSoftwareIsEstimate  (text: "true" | "false")
 *   KeyMetricFinancing
 *   KeyMetricAI
 *   KeyMetricEarnings            (sum of financing + software + grants + AI)
 *   KeyMetricsComputedAt         (date with time)
 */

import { find_grants, parseAmount } from './upgradeListUtils.js';
import { getNonFreebieGrants } from './grantListSort.js';
import {
  sumSoftwareSavingsFromMatchedDiscounts,
  defaultSoftwareSavingsAnnualEstimate,
} from './softwareSavingsTotals.js';

export const GRANTS_KEY_METRIC_FALLBACK = 2138283;
export const REFERRALS_KEY_METRIC_POTENTIAL = 31440;
export const SUMMARY_PRIORITY_GRANTS_CAP = 25;

/** Consider company-cached metrics stale after this many ms. */
export const KEY_METRICS_STALE_MS = 24 * 60 * 60 * 1000;

export const KEY_METRIC_COMPANY_FIELDS = {
  grants: 'KeyMetricGrants',
  software: 'KeyMetricSoftware',
  softwareIsEstimate: 'KeyMetricSoftwareIsEstimate',
  financing: 'KeyMetricFinancing',
  ai: 'KeyMetricAI',
  earnings: 'KeyMetricEarnings',
  computedAt: 'KeyMetricsComputedAt',
};

export function grantAmountForSummaryTotal(grant) {
  const f = grant?.fields || {};
  let n = f.ExpectedValue;
  if (n != null && n !== '') {
    const nv = typeof n === 'number' ? n : parseFloat(String(n).replace(/[$,]/g, ''));
    if (!Number.isNaN(nv) && nv > 0) return nv;
  }
  n = f.AnnualMargin;
  if (n != null && n !== '') {
    const nv = typeof n === 'number' ? n : parseFloat(String(n).replace(/[$,]/g, ''));
    if (!Number.isNaN(nv) && nv > 0) return nv;
  }
  const amt = parseAmount(f.Amount);
  if (amt > 0) {
    const ps = parseFloat(f.pSuccess);
    const p = !Number.isNaN(ps) && ps > 0 ? ps : 0.65;
    return amt * p;
  }
  return 0;
}

export function coerceAnnualRevenue(companyFields, defaultRevenue = 100000) {
  const v = companyFields?.AnnualRevenue;
  if (v != null && v !== '' && v !== 'n/a') {
    const n = Number(v);
    if (!Number.isNaN(n)) return n;
  }
  return defaultRevenue * 12;
}

export function coerceEmployees(companyFields, defaultEmployees = 10) {
  const v = companyFields?.Employees;
  if (v != null && v !== '' && v !== 'n/a') {
    const n = Number(v);
    if (!Number.isNaN(n)) return n;
  }
  return defaultEmployees;
}

export function computeFinancing(companyFields, defaultRevenue = 100000) {
  const annualRevenue = coerceAnnualRevenue(companyFields, defaultRevenue);
  const debt = Number(companyFields?.Debt) || 0;
  return annualRevenue * 0.4 - debt;
}

export function computeAi(companyFields, defaultEmployees = 10) {
  const employees = coerceEmployees(companyFields, defaultEmployees);
  if (companyFields?.Employees != null && companyFields.Employees !== '' && companyFields.Employees !== 'n/a') {
    const n = Number(companyFields.Employees);
    if (!Number.isNaN(n)) return n * 22000;
  }
  return employees * 22000;
}

export function computeSoftwareFromMatched(matchedDiscounts = [], techSavings = 0, companyFields) {
  const fromList = sumSoftwareSavingsFromMatchedDiscounts(matchedDiscounts);
  if (matchedDiscounts.length > 0) {
    return { amount: fromList, isEstimate: false };
  }
  const t = typeof techSavings === 'number' ? techSavings : parseFloat(techSavings);
  if (Number.isFinite(t) && t > 0) {
    return { amount: t, isEstimate: false };
  }
  return {
    amount: defaultSoftwareSavingsAnnualEstimate(companyFields),
    isEstimate: true,
  };
}

/**
 * Grants total for key metrics: eligible → non-freebie priority → top N sum.
 * Returns null when inputs are insufficient (caller should skeleton, not invent).
 */
export function computeGrantsAmount(companyFields, grants = []) {
  if (!companyFields || !grants?.length) return null;

  const filtered =
    find_grants(
      companyFields.Country || '',
      companyFields.ProvinceOrState || '',
      companyFields.Address || '',
      companyFields.Founded || '',
      companyFields.Industry || '',
      companyFields.Employees || '',
      companyFields['Profitable?'] || '',
      grants,
      'both',
      companyFields.CompanyType || '',
      companyFields.IndigenousTeamMember === true,
      companyFields.WomenLed === true
    ) || [];

  if (!filtered.length) return 0;

  const interested = [];
  const flagged = [];
  const prioritized = getNonFreebieGrants(filtered, interested, flagged);
  const slice = prioritized.slice(0, SUMMARY_PRIORITY_GRANTS_CAP);
  const sum = slice.reduce((t, g) => t + grantAmountForSummaryTotal(g), 0);

  if (filtered.length > 0 && sum <= 0) {
    // Eligible but no parseable $ — keep null so UI can show pending/estimate label, not fake $2.1M
    return null;
  }
  return sum;
}

export function computeReferralsFromUserFields(userFields) {
  if (!userFields || typeof userFields !== 'object') return REFERRALS_KEY_METRIC_POTENTIAL;
  const parse = (v) => {
    if (v == null || v === '') return 0;
    if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
    return parseFloat(String(v).replace(/[$,]/g, '')) || 0;
  };
  const total = parse(userFields.referralPayout) + parse(userFields.NTMreferralPayout);
  return total > 0 ? total : REFERRALS_KEY_METRIC_POTENTIAL;
}

export function parseSoftwareIsEstimateFlag(value) {
  if (value === true || value === 1) return true;
  if (value === false || value === 0 || value == null || value === '') return false;
  const s = String(value).trim().toLowerCase();
  return s === 'true' || s === 'yes' || s === '1' || s === 'estimate' || s === 'y';
}

export function formatSoftwareIsEstimateFlag(isEstimate) {
  return isEstimate ? 'true' : 'false';
}

export function readCachedMetricsFromCompany(fields = {}) {
  const F = KEY_METRIC_COMPANY_FIELDS;
  const num = (v) => {
    if (v == null || v === '') return null;
    const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/[$,]/g, ''));
    return Number.isFinite(n) ? n : null;
  };
  const computedAtRaw = fields[F.computedAt];
  const computedAt = computedAtRaw ? new Date(computedAtRaw).getTime() : null;
  const ageMs = computedAt && !Number.isNaN(computedAt) ? Date.now() - computedAt : null;
  const stale = ageMs == null || ageMs > KEY_METRICS_STALE_MS;

  return {
    grants: num(fields[F.grants]),
    software: num(fields[F.software]),
    softwareIsEstimate: parseSoftwareIsEstimateFlag(fields[F.softwareIsEstimate]),
    financing: num(fields[F.financing]),
    ai: num(fields[F.ai]),
    earnings: num(fields[F.earnings]),
    computedAt: computedAt && !Number.isNaN(computedAt) ? new Date(computedAt).toISOString() : null,
    stale,
  };
}

export function sumEarningsTotal({ financing = 0, software = 0, grants = 0, ai = 0 }) {
  return (
    (Number(financing) || 0) +
    (Number(software) || 0) +
    (Number(grants) || 0) +
    (Number(ai) || 0)
  );
}

export function buildCompanyMetricsPatch({
  grants,
  software,
  softwareIsEstimate,
  financing,
  ai,
  earnings,
}) {
  const F = KEY_METRIC_COMPANY_FIELDS;
  const patch = {
    [F.computedAt]: new Date().toISOString(),
  };
  if (financing != null && Number.isFinite(financing)) patch[F.financing] = financing;
  if (ai != null && Number.isFinite(ai)) patch[F.ai] = ai;
  if (software != null && Number.isFinite(software)) {
    patch[F.software] = software;
    patch[F.softwareIsEstimate] = formatSoftwareIsEstimateFlag(softwareIsEstimate);
  }
  if (grants != null && Number.isFinite(grants)) patch[F.grants] = grants;

  // Only write earnings when we know grants (full picture) or caller passed an explicit total.
  // Explicit earnings is used for first-load fill (may be partial until grants exist).
  if (earnings != null && Number.isFinite(earnings)) {
    patch[F.earnings] = earnings;
  } else if (grants != null && Number.isFinite(grants)) {
    patch[F.earnings] = sumEarningsTotal({
      financing: financing ?? 0,
      software: software ?? 0,
      grants,
      ai: ai ?? 0,
    });
  }

  return patch;
}

/**
 * Build a full metrics payload from company (+ optional grants catalog / discounts / user).
 */
export function assembleKeyMetrics({
  companyFields,
  grants = null,
  matchedDiscounts = [],
  techSavings = 0,
  userFields = null,
  preferCache = true,
}) {
  const cached = readCachedMetricsFromCompany(companyFields || {});
  const financing = computeFinancing(companyFields);
  const ai = computeAi(companyFields);

  let software;
  let softwareIsEstimate;
  if (matchedDiscounts?.length > 0 || (techSavings && Number(techSavings) > 0)) {
    const s = computeSoftwareFromMatched(matchedDiscounts, techSavings, companyFields);
    software = s.amount;
    softwareIsEstimate = s.isEstimate;
  } else if (preferCache && cached.software != null) {
    software = cached.software;
    softwareIsEstimate = cached.softwareIsEstimate;
  } else {
    const s = computeSoftwareFromMatched([], 0, companyFields);
    software = s.amount;
    softwareIsEstimate = s.isEstimate;
  }

  let grantsAmount = null;
  if (Array.isArray(grants)) {
    grantsAmount = computeGrantsAmount(companyFields, grants);
  } else if (preferCache && cached.grants != null) {
    grantsAmount = cached.grants;
  }

  const earningsLive = sumEarningsTotal({
    financing,
    software,
    grants: grantsAmount ?? 0,
    ai,
  });
  const earnings =
    preferCache && cached.earnings != null && grantsAmount == null
      ? cached.earnings
      : earningsLive;

  return {
    financing,
    software,
    softwareIsEstimate,
    grants: grantsAmount,
    ai,
    earnings,
    referrals: computeReferralsFromUserFields(userFields),
    computedAt: cached.computedAt,
    stale: cached.stale,
    source: {
      financing: 'live',
      ai: 'live',
      software:
        matchedDiscounts?.length > 0 || Number(techSavings) > 0
          ? 'live'
          : preferCache && cached.software != null
            ? 'cache'
            : 'estimate',
      grants: Array.isArray(grants) ? 'live' : preferCache && cached.grants != null ? 'cache' : 'missing',
      earnings:
        preferCache && cached.earnings != null && grantsAmount == null ? 'cache' : 'live',
      referrals: userFields ? 'user' : 'default',
    },
  };
}
